import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {Block, Editor} from 'grapesjs';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {Icon} from '@Components/icon-v2';
import {Image} from 'react-bootstrap';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {TextBlockPanel} from '@Components/email-maker-block-manager/components/text-block-panel/text-block-panel';
import {ImageBlockPanel} from '@Components/email-maker-block-manager/components/image-block-panel';
import {ElementsBlockPanel} from '@Components/email-maker-block-manager/components/elements-block-panel';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {getUniqueString} from '@Utils/string.util';
import {SectionsBlockPanel} from '@Components/email-maker-block-manager/components/sections-block-panel';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {
  getAllEmailComponentTemplateCategories,
  getAllEmailComponentTemplates,
  getUserEmailComponentTemplates,
  hasUserSeenElementsBlock,
  hasUserSeenSectionsBlock,
  markUserHasSeenElementsBlock,
  markUserHasSeenSectionsBlock,
} from '@Libraries/email-component-template-library';
import type {BlockCategoryMap} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {setCategories, setSelectedBlockPanel, setTemplates, setUserTemplates} from '@Components/email-maker-block-manager/email-maker-block-manager.reducer';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {getAssetUrl} from '@Utils/s3.util';
import {GrapesjsEditorContext, IsMobileScreenContext} from './email-maker-block-manager-grapesjs-editor-context';
import {useAppDispatch} from '@/hooks';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {MobilePanelHeader} from '@Components/email-maker-block-manager/components/mobile-panel-header';
import {useSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import {GA4EventName} from '@Libraries/ga-events';
import styles from './email-maker-block-manager.module.scss';

export interface EmailMakerBlockManagerProps {
  gjsEditor: Editor;
}

export const POPUP_PANEL_ID = 'email-maker-popup-panel-id';

const PANEL_OFFSET_LEFT = 6;
const PANEL_ARROW_OFFSET = -6;

const INTRO_POPUP_OFFSET_LEFT = 41;
const INTRO_POPUP_ARROW_OFFSET = 31;
const INTRO_POPUP_SHOW_DELAY_MS = 0;
const INTRO_POPUP_HIDE_DELAY_MS = 100;

const POPUP_TOP_BOTTOM_MARGIN = 16;
const NAVBAR_HEIGHT = 60;
const POPUP_HEIGHT = window.innerHeight - NAVBAR_HEIGHT - 2 * POPUP_TOP_BOTTOM_MARGIN;

const DEFAULT_DEBOUNCE_TIMEOUT = 50;
const MOBILE_VERSION_BREAKPOINT = 768;

export function EmailMakerBlockManager({gjsEditor}: EmailMakerBlockManagerProps): ReactElement<any> {
  const blockManager = gjsEditor.BlockManager;
  const dispatch = useAppDispatch();
  const selectedCategory = useSelectedBlockPanel();

  const [hasUserSeenSections, setHasUserSeenSections] = useState<boolean>(true);
  const [hasUserSeenElements, setHasUserSeenElements] = useState<boolean>(true);
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(false);

  const BLOCK_CONSTANTS = window.PMW.emails.blocks;
  const CATEGORIES_WITH_PANEL = [BLOCK_CONSTANTS.CATEGORY_SECTIONS, BLOCK_CONSTANTS.CATEGORY_ELEMENTS, BLOCK_CONSTANTS.CATEGORY_TEXT, BLOCK_CONSTANTS.CATEGORY_IMAGE];
  const CATEGORIES_WITHOUT_PANEL = [BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS];

  const blocksMap = blockManager.getAll().reduce((blockCategoryMap: BlockCategoryMap, item: Block) => {
    const category = item.getCategoryLabel();
    const categoryItem = blockCategoryMap[category];

    if (!categoryItem) {
      blockCategoryMap[category] = [item];
    } else {
      categoryItem.push(item);
    }

    return blockCategoryMap;
  }, {} as BlockCategoryMap);

  Object.keys(blocksMap).forEach((category) => {
    blocksMap[category].sort((block1, block2) => {
      const sortPriority1 = block1.get('attributes')?.sortPriority as number;
      const sortPriority2 = block2.get('attributes')?.sortPriority as number;
      return sortPriority1 - sortPriority2;
    });
  });

  const getIcon = (category: string): string => {
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return 'icon-section';
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return 'icon-elements';
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return 'icon-text';
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return 'icon-image';
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return 'icon-designs';
      default:
        return 'icon-exclamation-triangle';
    }
  };

  const getTextLabel = (category: string): string => {
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return window.i18next.t('pmwjs_sections');
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return window.i18next.t('pmwjs_elements');
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text');
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image');
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return window.i18next.t('pmwjs_my_designs');
      default:
        return 'Unknown Block';
    }
  };

  const getDescription = (category: string): string => {
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return window.i18next.t('pmwjs_sections_block_description');
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return window.i18next.t('pmwjs_elements_block_description');
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text_block_description');
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image_block_description');
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return window.i18next.t('pmwjs_my_designs_block_description');
      default:
        return 'Unknown Block';
    }
  };

  const getIntroPopupImage = (category: string): ReactElement<any> => {
    let imageSource = '';
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        imageSource = 'section-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        imageSource = 'elements-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        imageSource = 'text-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        imageSource = 'image-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        imageSource = 'my-designs-block-intro.png';
        break;
    }

    return <Image src={getAssetUrl(imageSource)} alt={window.i18next.t('pmwjs_image')} aria-hidden />;
  };

  const getIntroPopupDescription = (category: string): ReactElement<any> => {
    let description = '';
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        description = window.i18next.t('pmwjs_sections_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        description = window.i18next.t('pmwjs_elements_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        description = window.i18next.t('pmwjs_text_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        description = window.i18next.t('pmwjs_image_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        description = window.i18next.t('pmwjs_my_designs_block_intro');
        break;
    }

    return <Text val={description} size={TextSize.XSMALL} />;
  };

  const getActivityIndicator = (category: string): ReactElement<any> | null => {
    if ((category === BLOCK_CONSTANTS.CATEGORY_SECTIONS && !hasUserSeenSections) || (category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS && !hasUserSeenElements)) {
      return <PulsatingDot containerClassname={styles.activityIndicator} />;
    }

    return null;
  };

  const getBlockIntroPopup = (category: string): ReactElement<any> => {
    const popup = (
      <div className={styles.introPopup}>
        {getIntroPopupImage(category)}
        {getIntroPopupDescription(category)}
      </div>
    );

    return (
      <div className={styles.introPopupContainer}>
        <Dropdown
          open
          popup={popup}
          alignment={ALIGNMENT_TYPE.RIGHT_START}
          selector={<div />}
          position={DROPDOWN_POSITION.ABSOLUTE}
          useDefaultArrow
          leftOffset={INTRO_POPUP_OFFSET_LEFT}
          arrowOffset={INTRO_POPUP_ARROW_OFFSET}
          popUpHasCustomWidth
          popUpHasCustomHeight
          arrowClasses={styles.popupPanelArrow}
          popupClassName={`${styles.popupPanel} ${styles.dynamicHeight}`}
        />
      </div>
    );
  };

  const getSelectorComponentLabelAndIcon = (category: string, extraClasses = ''): ReactElement<any> => {
    const isSelected = selectedCategory === category;
    return (
      <div className={`${styles.block} ${extraClasses} ${isSelected ? styles.selected : ''}`}>
        {getActivityIndicator(category)}
        <Icon icon={getIcon(category)} size={IconSize.SIZE_ICON_24} />
        <Text val={getTextLabel(category)} size={TextSize.XXSMALL} bold={isSelected} />
      </div>
    );
  };

  const getSelectorComponent = (category: string, extraClasses = ''): ReactElement<any> => {
    return (
      <div>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          delay={{show: INTRO_POPUP_SHOW_DELAY_MS, hide: INTRO_POPUP_HIDE_DELAY_MS}}
          overlay={getBlockIntroPopup(category)}
          show={selectedCategory ? false : undefined}
        >
          {getSelectorComponentLabelAndIcon(category, extraClasses)}
        </OverlayTrigger>
      </div>
    );
  };

  const getDefaultMobilePanel = (): ReactElement<any> => {
    return (
      <>
        <div className={styles.mobilePanelHeader}>
          <MobilePanelHeader headerLabel={window.i18next.t('pmwjs_add_to_email')} />
        </div>
        <div className={styles.mobileAllBlocks}>{getAllBlocks()}</div>
      </>
    );
  };

  const getPanelContent = (category: string | null): ReactElement<any> => {
    const props = {
      blocksMap,
      showBackButton: isMobileScreen,
      onBackButton: (): void => {
        dispatch(setSelectedBlockPanel(null));
      },
    };

    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return <SectionsBlockPanel {...props} headerLabel={window.i18next.t('pmwjs_sections')} />;
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return <ElementsBlockPanel {...props} headerLabel={window.i18next.t('pmwjs_elements')} />;
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return <TextBlockPanel {...props} headerLabel={isMobileScreen ? window.i18next.t('pmwjs_text') : undefined} />;
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return <ImageBlockPanel {...props} headerLabel={window.i18next.t('pmwjs_image')} />;
      default:
        return <div>{isMobileScreen ? getDefaultMobilePanel() : <> Panel Content not defined </>}</div>;
    }
  };

  const getBlockWithoutPanel = (category: string): ReactElement<any> => {
    return (
      <>
        {blocksMap[category].map((block) => {
          return (
            <EmailMakerBlock block={block} extraClasses={`${styles.block} spacing-p-0`} key={getUniqueString()}>
              {getSelectorComponent(block.getCategoryLabel())}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getBlockWithPanel = (category: string): ReactElement<any> => {
    const popupClasses = `${styles.popup} ${category === BLOCK_CONSTANTS.CATEGORY_SECTIONS ? styles.sectionsPopup : ''} ${category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS ? styles.elementsPopup : ''}`;
    const popup = <div className={popupClasses}>{getPanelContent(category)}</div>;

    const openHandler = (): void => {
      dispatch(setSelectedBlockPanel(category));
      markSectionAsSeen(category);
      trackGA4EventOnPanelOpen(category);
    };

    const closeHandler = (): void => {
      dispatch(setSelectedBlockPanel(null));
    };

    return (
      <Dropdown
        open={selectedCategory === category}
        doCloseOnInnerClick={false}
        doesCloseOnOuterClick={false}
        alignment={ALIGNMENT_TYPE.RIGHT}
        position={DROPDOWN_POSITION.ABSOLUTE}
        selector={getSelectorComponent(category, styles.subSectionBlock)}
        popup={popup}
        maxHeight={POPUP_HEIGHT}
        leftOffset={PANEL_OFFSET_LEFT}
        arrowOffset={PANEL_ARROW_OFFSET}
        popUpHasCustomWidth
        useDefaultArrow
        arrowClasses={styles.popupPanelArrow}
        popupClassName={`${styles.popupPanel} ${category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS ? styles.dynamicHeight : ''}`}
        popupId={POPUP_PANEL_ID}
        popupSpacingClass={styles.popupPanelSpacing}
        key={getUniqueString()}
        onOpen={openHandler}
        onClose={closeHandler}
      />
    );
  };

  const getMobileBlockVisualComponent = (category: string): ReactElement<any> => {
    return (
      <div className={styles.mobileBlockVisualContainer}>
        <div className={styles.mobileIconContainer}>
          <div className={styles.mobileIconShadowContainer}>
            <Icon icon={getIcon(category)} className={styles.mobileIcon} size={IconSize.SIZE_ICON_16} />
          </div>
        </div>

        <div className={styles.mobileBlockTextContainer}>
          <Text val={getTextLabel(category)} size={TextSize.XSMALL} bold />
          <Text val={getDescription(category)} size={TextSize.XXSMALL} />
        </div>
      </div>
    );
  };

  const getMobileBlockWithPanel = (category: string): ReactElement<any> => {
    return (
      <ClickableDiv
        key={getUniqueString()}
        onClick={() => {
          dispatch(setSelectedBlockPanel(category));
        }}
      >
        {getMobileBlockVisualComponent(category)}
      </ClickableDiv>
    );
  };

  const getMobileBlockWithoutPanel = (category: string): ReactElement<any> => {
    return (
      <>
        {blocksMap[category].map((block) => {
          return (
            <EmailMakerBlock block={block} key={getUniqueString()}>
              {getMobileBlockVisualComponent(category)}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getAllBlocks = (): ReactElement<any> => {
    const blockWithPanel = (category: string): ReactElement<any> => {
      if (isMobileScreen) {
        return getMobileBlockWithPanel(category);
      }

      return getBlockWithPanel(category);
    };

    const blockWithoutPanel = (category: string): ReactElement<any> => {
      if (isMobileScreen) {
        return getMobileBlockWithoutPanel(category);
      }

      return getBlockWithoutPanel(category);
    };

    return (
      <>
        {CATEGORIES_WITH_PANEL.map((category) => {
          return blockWithPanel(category);
        })}
        {CATEGORIES_WITHOUT_PANEL.map((category) => {
          return blockWithoutPanel(category);
        })}
      </>
    );
  };

  const markSectionAsSeen = (category: string): void => {
    if (category === BLOCK_CONSTANTS.CATEGORY_SECTIONS && !hasUserSeenSections) {
      setHasUserSeenSections(true);
      void markUserHasSeenSectionsBlock();
    } else if (category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS && !hasUserSeenElements) {
      setHasUserSeenElements(true);
      void markUserHasSeenElementsBlock();
    }
  };

  const fetchAllDataIntoRedux = async (): Promise<void> => {
    await Promise.all([getAllEmailComponentTemplateCategories(), getAllEmailComponentTemplates(), getUserEmailComponentTemplates()]).then(
      ([categories, templates, userTemplates]) => {
        dispatch(setCategories(categories));
        dispatch(setTemplates(templates));
        dispatch(setUserTemplates(userTemplates));
      }
    );
  };

  const isMobileScreenWidth = (screenWidth: number): boolean => {
    return screenWidth <= MOBILE_VERSION_BREAKPOINT;
  };

  const resizeHandler = window.PMW.debounce((): void => {
    const isCurrentWindowWidthMobile = isMobileScreenWidth(window.innerWidth);
    setIsMobileScreen(isCurrentWindowWidthMobile);
  }, DEFAULT_DEBOUNCE_TIMEOUT);

  const closePopupListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!document.getElementById(window.PMW.emails.blocks.ID_BLOCKS_MANAGER_CONTAINER)?.contains(target)) {
      dispatch(setSelectedBlockPanel(null));
    }
  };

  const trackGA4EventOnPanelOpen = (category: string): void => {
    if (category === BLOCK_CONSTANTS.CATEGORY_SECTIONS) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_ADD_SECTION);
    }
  };

  useEffect(() => {
    void hasUserSeenSectionsBlock().then((hasSeen) => {
      setHasUserSeenSections(hasSeen);
    });
    void hasUserSeenElementsBlock().then((hasSeen) => {
      setHasUserSeenElements(hasSeen);
    });
    void fetchAllDataIntoRedux();

    resizeHandler();
    window.addEventListener('resize', resizeHandler, {passive: true});
    document.addEventListener('touchstart', closePopupListener);
    document.addEventListener('mousedown', closePopupListener);

    return (): void => {
      resizeHandler.clear();
      window.removeEventListener('resize', resizeHandler);
      document.removeEventListener('touchstart', closePopupListener);
      document.removeEventListener('mousedown', closePopupListener);
    };
  }, []);

  const getBlockManager = (): ReactElement<any> => {
    if (isMobileScreen) {
      return <IsMobileScreenContext.Provider value={isMobileScreen}>{getPanelContent(selectedCategory)}</IsMobileScreenContext.Provider>;
    }

    return (
      <div className={styles.blocksManager} key={getUniqueString()}>
        {getAllBlocks()}
      </div>
    );
  };

  return <GrapesjsEditorContext.Provider value={gjsEditor}>{getBlockManager()}</GrapesjsEditorContext.Provider>;
}
